import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { AlertCircle, ChevronLeft, ChevronRight, Plus, CheckCircle2, Trash2, ShieldAlert, Loader2, ArrowRight } from 'lucide-react';
import api from '../api/client';
import { useToast } from '../hooks/use-toast';

/**
 * 5-Step "New Equipment Request" wizard.
 *
 * Field-mapping to the existing backend (kept intact, no table changes):
 *   Step 1  Project ID       → project_name        (dropdown master list)
 *           Company Name     → company_name
 *           Location         → project_location
 *   Step 2  Category         → requested_category
 *           Type             → requested_type      (new optional Pydantic field)
 *           Capacity         → requested_capacity  (new optional Pydantic field)
 *           Date Needed      → date_needed         (anti-backdate min = today Manila)
 *   Step 3  Pick specific vehicles matching Step 2 filters. Each pick becomes
 *           an entry in `preferred_vehicle_ids`. If nothing matches, the user
 *           is redirected to Equipment Queues via "Forward to Queuing".
 *   Step 4  Summary of collected units. Loop back to Step 3 or go to Step 5.
 *   Step 5  Full available-list review, final confirmation, submit.
 *
 * The wizard preserves state across steps in `formState` so Step 4's loop
 * back to Step 3 never wipes prior picks.
 */

// Deployment TZ = Asia/Manila (GMT+8, no DST). Used by the Date Needed
// picker's `min` attribute so it aligns with the server anti-backdate rule.
const todayManila = () => {
  const ms = Date.now() + 8 * 60 * 60 * 1000;
  return new Date(ms).toISOString().slice(0, 10);
};

const StepIndicator = ({ current, total = 5 }) => (
  <div className="flex items-center gap-2" data-testid="wizard-step-indicator">
    {Array.from({ length: total }).map((_, i) => {
      const n = i + 1;
      const active = n === current;
      const done = n < current;
      return (
        <React.Fragment key={n}>
          <div
            className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
              done
                ? 'bg-emerald-600 text-white'
                : active
                ? 'bg-[hsl(217,91%,24%)] text-white ring-4 ring-[hsl(217,91%,24%)]/15'
                : 'bg-slate-200 text-slate-500'
            }`}
          >
            {done ? <CheckCircle2 className="h-4 w-4" /> : n}
          </div>
          {n < total && <div className={`h-0.5 flex-1 ${done ? 'bg-emerald-600' : 'bg-slate-200'}`} />}
        </React.Fragment>
      );
    })}
  </div>
);

const AdminWarning = () => (
  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="wizard-admin-warning">
    <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
    <span>
      All fields must be filled. Items not found in the drop-down menu are either unavailable or non-existent and must be coordinated with the administrator.
    </span>
  </div>
);

const Dropdown = ({ label, value, onChange, options, testId, placeholder = 'Select…' }) => (
  <div className="space-y-1">
    <Label className="text-xs font-medium text-slate-700">{label}</Label>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="h-9" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {(options || []).length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-slate-500">No options — please contact admin.</div>
        ) : (
          options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)
        )}
      </SelectContent>
    </Select>
  </div>
);

const EMPTY_STATE = {
  // Step 1
  project_name: '',           // stores the picked "Project ID"
  company_name: '',
  project_location: '',       // mirrored from Project ID pick (same master list)
  // Step 2 — Category, Type, Capacity all sourced from live fleet distinct
  // values (via /api/vehicles/spec-options). Date Needed uses anti-backdate.
  requested_category: '',
  requested_type: '',
  requested_capacity: '',
  date_needed: '',
  // Step 3 running selection
  preferred_vehicle_ids: [],
  // Full details cache keyed by vehicle_id — populated on Step-3 save so that
  // Step 4 / Step 5 can display already-picked units even after the user
  // loops back to Step 2 and changes filters (which replaces availableVehicles).
  picked_details: {},
  // Currently in-progress Step-3 entry (before user clicks NEXT to save it).
  pending_vehicle_id: '',
  pending_remarks: '',
};

export const NewRequestWizard = ({ open, onOpenChange, dropdowns, onSubmitted }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formState, setFormState] = useState(EMPTY_STATE);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Step 2 dropdown options come from live fleet specs (distinct values on
  // the Equipment collection) so a user can only pick specifications that
  // actually exist somewhere in the fleet.
  const [specOptions, setSpecOptions] = useState({ types: [], capacities: [] });

  // Reset wizard when it closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setStep(1);
      setFormState(EMPTY_STATE);
      setAvailableVehicles([]);
    }
  }, [open]);

  // Fetch Step 2 master lists once when the wizard opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/vehicles/spec-options');
        if (!cancelled) setSpecOptions({
          categories: Array.isArray(r.data?.categories) ? r.data.categories : [],
          types: Array.isArray(r.data?.types) ? r.data.types : [],
          capacities: Array.isArray(r.data?.capacities) ? r.data.capacities : [],
        });
      } catch (e) {
        console.warn('spec-options load failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const setField = useCallback((patch) => setFormState((prev) => ({ ...prev, ...patch })), []);

  // ---- Step 1 validation ----
  // Project ID is stored in `project_name` and mirrored into `project_location`
  // (Step 1 dropdown auto-sets both). Only these two fields are user-picked
  // on Step 1 now — Location was folded into Project ID per the updated spec.
  const step1Valid = formState.project_name && formState.company_name;

  // ---- Step 2 validation ----
  // Category + Date Needed are required. Type and Capacity are optional
  // refinements: when left blank the wizard matches by category only, so
  // uploaded equipment that has no Type/Capacity on file still appears.
  const step2Valid = formState.requested_category && formState.date_needed;

  // ---- Fetch ALL matching vehicles (Step 3) with is_available annotation ----
  // Step 3 lists the full inventory that matches Step 2 filters. Each unit
  // carries `is_available` — the UI conditionally offers NEXT or Forward-to-
  // Queuing based on the selection. Step 5's final list still shows only
  // available picks (the request is not accepted for non-available units).
  const fetchAvailable = useCallback(async () => {
    setLoadingAvail(true);
    try {
      const params = new URLSearchParams({
        category: formState.requested_category || '',
        type: formState.requested_type || '',
        capacity: formState.requested_capacity || '',
      });
      const url = step === 3 ? `/vehicles/matching?${params.toString()}` : `/vehicles/available?${params.toString()}`;
      const r = await api.get(url);
      setAvailableVehicles(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast({ title: 'Could not load equipment', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      setAvailableVehicles([]);
    } finally {
      setLoadingAvail(false);
    }
  }, [formState.requested_category, formState.requested_type, formState.requested_capacity, step, toast]);

  useEffect(() => {
    // Re-query availability every time Step 3 or Step 5 becomes visible.
    if (open && (step === 3 || step === 5)) {
      fetchAvailable();
    }
  }, [open, step, fetchAvailable]);

  // ---- Step 3 save & remove ----
  // Persist the currently-in-progress entry (pending_vehicle_id + remarks)
  // into the accumulated arrays, then advance to Step 4 per spec.
  const saveEntryAndAdvance = () => {
    const vid = formState.pending_vehicle_id;
    if (!vid) {
      toast({ title: 'Please pick a piece of equipment before proceeding.', variant: 'destructive' });
      return;
    }
    if (formState.preferred_vehicle_ids.includes(vid)) {
      toast({ title: 'That unit has already been added to this request.', variant: 'destructive' });
      return;
    }
    const detail = availableVehicles.find((v) => v.id === vid);
    // Defensive guard: the Step-3 footer already switches to a "Forward to
    // Queuing" button when the picked unit is unavailable, but we double-check
    // here so that no unavailable unit can be added to preferred_vehicle_ids.
    if (detail && detail.is_available === false) {
      toast({ title: 'This unit is not available — please forward to Equipment Queuing instead.', variant: 'destructive' });
      return;
    }
    setFormState((prev) => ({
      ...prev,
      preferred_vehicle_ids: [...prev.preferred_vehicle_ids, vid],
      picked_details: {
        ...prev.picked_details,
        [vid]: {
          id: vid,
          equipment_code: detail?.equipment_code || vid,
          brand: detail?.brand || '',
          model: detail?.model || '',
          plate_no: detail?.plate_no || '',
          serial_no: detail?.serial_no || '',
          type: detail?.type || '',
          capacity: detail?.capacity || '',
          remarks: (prev.pending_remarks || '').trim(),
        },
      },
      pending_vehicle_id: '',
      pending_remarks: '',
    }));
    setStep(4);
  };

  const removeVehicle = (id) => setFormState((prev) => {
    const nextDetails = { ...prev.picked_details };
    delete nextDetails[id];
    return {
      ...prev,
      preferred_vehicle_ids: prev.preferred_vehicle_ids.filter((x) => x !== id),
      picked_details: nextDetails,
    };
  });

  // Selected units are derived from `picked_details` (cached at Step-3 save
  // time) so they survive Step-2 loop-backs that swap out `availableVehicles`.
  const selectedVehicles = formState.preferred_vehicle_ids
    .map((id) => formState.picked_details[id])
    .filter(Boolean);

  // ---- Forward to Queuing (Step 3 unavailable-pick OR empty-matches flow) ----
  // Persists the collected form data:
  //   • available picks (if any) → a normal equipment_request (status=pending)
  //   • unavailable pick (if any) → a queued equipment_request + equipment_queues entry
  //   • no pick at all (empty Step-3 list) → a queued request + queue entry for
  //     the unsourced category need, so the request still lands in Queuing
  // Then closes the modal and routes to /equipment-queues.
  const forwardToQueue = async () => {
    const vid = formState.pending_vehicle_id || null; // null = no unit matched
    setSubmitting(true);
    try {
      const payload = {
        project_name: formState.project_name,
        project_location: formState.project_location,
        company_name: formState.company_name,
        requested_category: formState.requested_category,
        requested_type: formState.requested_type,
        requested_capacity: formState.requested_capacity,
        date_needed: formState.date_needed,
        available_vehicle_ids: formState.preferred_vehicle_ids,
        unavailable_vehicle_id: vid,
        unavailable_remarks: (formState.pending_remarks || '').trim() || null,
      };
      const r = await api.post('/equipment-requests/queue-forward', payload);
      const q = r.data?.queue_entry;
      const queued = r.data?.queued_request;
      const normal = r.data?.normal_request;
      const parts = [queued && `Queued request ${queued.reference_number}`, normal && `Pending request ${normal.reference_number}`].filter(Boolean).join(' · ');
      toast({
        title: 'Forwarded to Equipment Queues',
        description: parts || `Queue ref ${q?.reference_number}`,
      });
      onOpenChange(false);
      if (typeof onSubmitted === 'function') onSubmitted(r.data);
      navigate('/equipment-queues');
    } catch (e) {
      toast({ title: 'Forward failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Final submit ----
  const submit = async () => {
    if (formState.preferred_vehicle_ids.length === 0) {
      toast({ title: 'Add at least one equipment before submitting', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      // Compose per-unit remarks (captured during each Step-3 form pass)
      // into the existing `technical_specs` free-text field, so no schema
      // change is required to keep the notes.
      const remarksBlob = (formState.preferred_vehicle_ids || [])
        .map((vid, idx) => {
          const d = formState.picked_details[vid];
          const label = `Unit ${idx + 1} (${d?.equipment_code || vid})`;
          const note = d?.remarks || '';
          return note ? `${label}: ${note}` : null;
        })
        .filter(Boolean)
        .join('\n');
      const payload = {
        project_name: formState.project_name,
        project_location: formState.project_location,
        company_name: formState.company_name,
        requested_category: formState.requested_category,
        requested_type: formState.requested_type,
        requested_capacity: formState.requested_capacity,
        date_needed: formState.date_needed,
        preferred_vehicle_ids: formState.preferred_vehicle_ids,
        technical_specs: remarksBlob || undefined,
        // quantity is derived server-side from preferred_vehicle_ids.length
        quantity: formState.preferred_vehicle_ids.length,
      };
      const r = await api.post('/equipment-requests', payload);
      toast({
        title: 'Request submitted',
        description: `Reference ${r.data?.reference_number} · ${formState.preferred_vehicle_ids.length} unit(s).`,
      });
      onOpenChange(false);
      if (typeof onSubmitted === 'function') onSubmitted(r.data);
    } catch (e) {
      toast({ title: 'Submit failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="new-request-wizard">
        <DialogHeader>
          <DialogTitle className="text-[hsl(217,91%,24%)]">Equipment Request Form</DialogTitle>
          <DialogDescription>
            Step {step} of 5 · {['Basic Information', 'Equipment Specifications', 'Equipment Information', 'Review & Loop', 'Availability & Submit'][step - 1]}
          </DialogDescription>
          <div className="pt-3"><StepIndicator current={step} /></div>
        </DialogHeader>

        {/* ---------- STEP 1 ---------- */}
        {step === 1 && (
          <div className="space-y-4 py-2" data-testid="wizard-step-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Dropdown
                label="Project ID *"
                value={formState.project_name}
                // Project ID now uses the same master list as Location (per spec:
                // "Project ID must be equal to Location"). We store the picked
                // value in BOTH `project_name` and `project_location` so the
                // backend contract (project_name required) plus any downstream
                // filters that read project_location keep working unchanged.
                onChange={(v) => setField({ project_name: v, project_location: v })}
                options={dropdowns.project_location || []}
                testId="w-project-id"
              />
              <Dropdown
                label="Company Name *"
                value={formState.company_name}
                onChange={(v) => setField({ company_name: v })}
                options={dropdowns.company_assigned || []}
                testId="w-company"
              />
            </div>
            <AdminWarning />
          </div>
        )}

        {/* ---------- STEP 2 ---------- */}
        {step === 2 && (
          <div className="space-y-4 py-2" data-testid="wizard-step-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Dropdown
                label="Category *"
                value={formState.requested_category}
                onChange={(v) => setField({ requested_category: v })}
                options={specOptions.categories}
                testId="w-category"
              />
              <Dropdown
                label="Type (optional)"
                value={formState.requested_type}
                onChange={(v) => setField({ requested_type: v })}
                options={specOptions.types}
                testId="w-type"
              />
              <Dropdown
                label="Capacity (optional)"
                value={formState.requested_capacity}
                onChange={(v) => setField({ requested_capacity: v })}
                options={specOptions.capacities}
                testId="w-capacity"
              />
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-700">Date Needed at site *</Label>
                <Input
                  type="date"
                  min={todayManila()}
                  value={formState.date_needed}
                  onChange={(e) => setField({ date_needed: e.target.value })}
                  data-testid="w-date-needed"
                />
                <p className="text-[10px] text-slate-500">Backdated requests are not allowed — date must be today or later (Asia/Manila).</p>
              </div>
            </div>
            <AdminWarning />
          </div>
        )}

        {/* ---------- STEP 3 ---------- */}
        {step === 3 && (
          <div className="space-y-4 py-2" data-testid="wizard-step-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
              <span className="font-semibold text-slate-700">Matching filters:</span>{' '}
              <span className="text-slate-600">{formState.requested_category} · {formState.requested_type} · {formState.requested_capacity}</span>
            </div>

            {loadingAvail ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading equipment…
              </div>
            ) : availableVehicles.filter((v) => !formState.preferred_vehicle_ids.includes(v.id)).length === 0 ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800 space-y-3" data-testid="wizard-no-availability">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">No equipment matches these specifications{formState.preferred_vehicle_ids.length > 0 ? ' (all matching units have already been added)' : ''}.</div>
                    <div className="text-xs mt-1">You may forward this request to the Equipment Queue so a supervisor can source or reassign a unit.</div>
                  </div>
                </div>
                <Button
                  onClick={forwardToQueue}
                  disabled={submitting}
                  variant="outline"
                  className="border-red-300 text-red-800 hover:bg-red-100"
                  data-testid="wizard-forward-to-queue"
                >
                  <ArrowRight className="h-4 w-4 mr-1.5" /> {submitting ? 'Forwarding…' : 'Forward to Queuing'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Dropdown of ALL matching units (available + unavailable). Unavailable
                    entries stay pickable so the user can still see the fleet, but
                    picking one triggers the "Forward to Queuing" flow below. */}
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-700">Equipment ID *</Label>
                  <Select
                    value={formState.pending_vehicle_id || ''}
                    onValueChange={(v) => setField({ pending_vehicle_id: v })}
                  >
                    <SelectTrigger className="h-9" data-testid="w-pending-vehicle">
                      <SelectValue placeholder="Select a unit…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableVehicles
                        .filter((v) => !formState.preferred_vehicle_ids.includes(v.id))
                        .map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            <span className={v.is_available ? '' : 'text-red-600'}>
                              {v.equipment_code} — {v.brand || ''} {v.model || ''} ({v.plate_no || 'NA'})
                              {v.is_available ? '' : ` · ${v.unavailable_reason || 'unavailable'}`}
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500">All units matching your specs are shown. Units already mobilized or non-operational will be routed to Equipment Queues instead of the request queue.</p>
                </div>

                {/* Read-only detail panel populated from the picked unit */}
                {(() => {
                  const pending = availableVehicles.find((v) => v.id === formState.pending_vehicle_id);
                  if (!pending) return null;
                  return (
                    <div className="space-y-2" data-testid="w-pending-detail">
                      <div className={`grid grid-cols-1 md:grid-cols-2 gap-2 text-xs rounded-md border p-3 ${pending.is_available ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40'}`}>
                        <div className="md:col-span-2 flex items-center gap-2">
                          {pending.is_available ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold" data-testid="w-pending-status-available">
                              <CheckCircle2 className="h-3 w-3" /> AVAILABLE
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[10px] font-semibold" data-testid="w-pending-status-unavailable">
                              <AlertCircle className="h-3 w-3" /> UNAVAILABLE
                              {pending.unavailable_reason ? ` · ${pending.unavailable_reason}` : ''}
                            </span>
                          )}
                        </div>
                        <div><span className="font-semibold text-slate-700">Equipment Name:</span> {pending.brand} {pending.model || ''}</div>
                        <div><span className="font-semibold text-slate-700">Plate No:</span> <span className="font-mono">{pending.plate_no || 'NA'}</span></div>
                        <div><span className="font-semibold text-slate-700">Serial No:</span> <span className="font-mono">{pending.serial_no || '—'}</span></div>
                        <div><span className="font-semibold text-slate-700">Engine No:</span> <span className="font-mono">{pending.engine_no || '—'}</span></div>
                        <div><span className="font-semibold text-slate-700">Chassis No:</span> <span className="font-mono">{pending.chassis_no || '—'}</span></div>
                        <div><span className="font-semibold text-slate-700">Area:</span> {pending.area || '—'}</div>
                      </div>
                    </div>
                  );
                })()}

                {/* Condition / Remarks — user-editable only when unit is available */}
                {(() => {
                  const pending = availableVehicles.find((v) => v.id === formState.pending_vehicle_id);
                  if (!pending || !pending.is_available) return null;
                  return (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-slate-700">Condition / Remarks</Label>
                      <Textarea
                        rows={3}
                        value={formState.pending_remarks}
                        onChange={(e) => setField({ pending_remarks: e.target.value })}
                        placeholder="e.g. Requires PMS before dispatch, deliver with operator manual, etc."
                        data-testid="w-pending-remarks"
                      />
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ---------- STEP 4 ---------- */}
        {step === 4 && (
          <div className="space-y-4 py-2" data-testid="wizard-step-4">
            <div className="text-sm text-slate-700 font-medium">
              {selectedVehicles.length} unit{selectedVehicles.length === 1 ? '' : 's'} added to this request:
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {selectedVehicles.length === 0 && (
                <div className="text-center text-xs text-slate-500 py-6 border border-dashed border-slate-300 rounded-md">
                  No units added yet — go back to Step 3 to select one.
                </div>
              )}
              {selectedVehicles.map((v) => (
                <div key={v.id} className="flex items-start justify-between border border-slate-200 rounded-md p-2.5 bg-emerald-50/40">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span className="font-mono text-xs">{v.equipment_code}</span>
                      <span className="text-slate-600 truncate">{v.brand} {v.model}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 pl-6">{v.plate_no || 'NA'} · {v.type || '—'} · {v.capacity || '—'}</div>
                    {v.remarks && (
                      <div className="text-[11px] text-slate-700 pl-6 mt-1 whitespace-pre-wrap"><span className="font-semibold">Remarks:</span> {v.remarks}</div>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeVehicle(v.id)} data-testid={`w-remove-vehicle-${v.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---------- STEP 5 ---------- */}
        {step === 5 && (
          <div className="space-y-4 py-2" data-testid="wizard-step-5">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
              <div><span className="font-semibold">Project ID:</span> {formState.project_name}</div>
              <div><span className="font-semibold">Company:</span> {formState.company_name}</div>
              <div><span className="font-semibold">Category / Type / Capacity:</span> {formState.requested_category} · {formState.requested_type} · {formState.requested_capacity}</div>
              <div><span className="font-semibold">Date Needed:</span> {formState.date_needed}</div>
              <div><span className="font-semibold">Units requested:</span> {formState.preferred_vehicle_ids.length}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-2">Selected equipment (final):</div>
              {loadingAvail ? (
                <div className="text-slate-500 text-xs">Re-validating availability…</div>
              ) : (
                <div className="space-y-1.5" data-testid="wizard-final-list">
                  {selectedVehicles.map((v) => (
                    <div key={v.id} className="flex items-center gap-2 text-xs border border-emerald-200 bg-emerald-50/40 rounded p-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="font-mono">{v.equipment_code}</span>
                      <span className="text-slate-600 truncate">{v.brand} {v.model}</span>
                      <span className="ml-auto text-slate-500">{v.plate_no || 'NA'} · {v.capacity || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              {formState.preferred_vehicle_ids.length > selectedVehicles.length && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                  Some earlier picks are no longer available and have been dropped. Please review.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ---------- FOOTER ---------- */}
        <DialogFooter className="pt-4 border-t mt-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wizard-cancel">Cancel</Button>
              <Button
                onClick={() => setStep(2)}
                disabled={!step1Valid}
                className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"
                data-testid="wizard-next-1"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-2">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                disabled={!step2Valid}
                className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"
                data-testid="wizard-next-2"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          )}
          {step === 3 && (() => {
            const pending = availableVehicles.find((v) => v.id === formState.pending_vehicle_id);
            const pickedUnavailable = !!(pending && !pending.is_available);
            return (
              <>
                <Button variant="outline" onClick={() => setStep(2)} data-testid="wizard-back-3">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                {pickedUnavailable ? (
                  <Button
                    onClick={forwardToQueue}
                    disabled={submitting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="wizard-forward-to-queue-btn"
                  >
                    <ArrowRight className="h-4 w-4 mr-1" /> {submitting ? 'Forwarding…' : 'Forward to Queuing'}
                  </Button>
                ) : (
                  <Button
                    onClick={saveEntryAndAdvance}
                    disabled={!formState.pending_vehicle_id}
                    className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"
                    data-testid="wizard-save-entry"
                  >
                    Save & Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </>
            );
          })()}
          {step === 4 && (
            <>
              <Button variant="outline" onClick={() => setStep(3)} data-testid="wizard-back-4">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                data-testid="wizard-add-more"
                className="border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Equipment
              </Button>
              <Button
                onClick={() => setStep(5)}
                disabled={selectedVehicles.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wizard-for-approval"
              >
                For Approval <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </>
          )}
          {step === 5 && (
            <>
              <Button variant="outline" onClick={() => setStep(4)} data-testid="wizard-back-5">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || selectedVehicles.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="wizard-submit"
              >
                {submitting ? 'Submitting…' : 'Confirm & Submit'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewRequestWizard;
